import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon, RepeatIcon } from "@chakra-ui/icons";
import { api } from "../api";

const formatRemaining = (validUntil) => {
  if (!validUntil) return "--:--";
  const ms = new Date(validUntil).getTime() - Date.now();
  if (ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

export default function InstructorAccessCodesPage() {
  const user = useUser(api);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("shareToken") || "";
  const shouldAutoGenerate = searchParams.get("generate") === "1";

  const [{ data, fetching, error }, getInstructorRollingShareCode] = useGlobalAction(api.getInstructorRollingShareCode);
  const [remaining, setRemaining] = useState("--:--");

  const loadCode = useCallback(async () => {
    try {
      await getInstructorRollingShareCode({
        ...(shareToken ? { shareToken } : {}),
      });
    } catch (loadError) {
      console.error("Failed to load instructor rolling code", loadError);
    }
  }, [getInstructorRollingShareCode, shareToken]);

  const generateCode = useCallback(async () => {
    try {
      await getInstructorRollingShareCode({
        ...(shareToken ? { shareToken } : {}),
        generate: true,
      });
    } catch (loadError) {
      console.error("Failed to generate instructor access code", loadError);
    }
  }, [getInstructorRollingShareCode, shareToken]);

  useEffect(() => {
    if (!user?.id) return;
    if (shouldAutoGenerate) {
      generateCode();
      return;
    }
    loadCode();
  }, [user?.id, shouldAutoGenerate, loadCode, generateCode]);

  useEffect(() => {
    const nextRemaining = formatRemaining(data?.validUntil);
    setRemaining(nextRemaining);

    const interval = setInterval(() => {
      setRemaining(formatRemaining(data?.validUntil));
    }, 1000);

    return () => clearInterval(interval);
  }, [data?.validUntil]);

  const codeGrouped = useMemo(() => {
    const code = String(data?.code || "");
    if (!code) return "";
    return `${code.slice(0, 4)} ${code.slice(4, 8)}`;
  }, [data?.code]);

  const handleCopyCode = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      toast({
        title: "Copied",
        description: "Rolling access code copied to clipboard.",
        status: "success",
        duration: 1800,
        isClosable: true,
      });
    } catch (copyError) {
      toast({
        title: "Copy failed",
        description: "Unable to copy code. Please copy it manually.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  if (!user) {
    return (
      <Container maxW="container.md" py={12}>
        <HStack spacing={3}>
          <Spinner />
          <Text>Loading session...</Text>
        </HStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={12}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">Instructor Rolling Access Code</Heading>
          <Text color="gray.400" mt={2}>
            Generate a 4-hour evaluator code when you need it. Share it verbally with evaluators.
          </Text>
        </Box>

        {(error || data?.success === false) && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Code unavailable</AlertTitle>
              <AlertDescription>
                {error?.message || data?.message || "Unable to load your rolling code."}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="lg" p={6}>
          {fetching && !data?.code ? (
            <HStack spacing={3}>
              <Spinner />
              <Text>Generating your current code...</Text>
            </HStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Text color="gray.400" fontSize="sm">Current access code</Text>
              <Text
                fontSize={{ base: "3xl", md: "4xl" }}
                letterSpacing="0.18em"
                fontWeight="800"
                fontFamily="monospace"
              >
                {codeGrouped || "--------"}
              </Text>
              <HStack justify="space-between" wrap="wrap" spacing={3}>
                <Text color="gray.400" fontSize="sm">
                  Expires in: <Text as="span" color="white" fontWeight="semibold">{remaining}</Text>
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Valid until: {data?.validUntil ? new Date(data.validUntil).toLocaleString() : "--"}
                </Text>
              </HStack>
              <HStack spacing={3}>
                <Button leftIcon={<CopyIcon />} onClick={handleCopyCode} isDisabled={!data?.code}>
                  Copy Code
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={generateCode}
                  isLoading={fetching}
                >
                  Generate New Code
                </Button>
                <Button
                  variant="outline"
                  leftIcon={<RepeatIcon />}
                  onClick={loadCode}
                  isLoading={fetching}
                >
                  Refresh
                </Button>
                <Button variant="ghost" onClick={() => navigate("/")}>Back to Home</Button>
              </HStack>
            </VStack>
          )}
        </Box>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Each generated code is valid for 4 hours and is attributed server-side to your instructor account for audit logs.
          </AlertDescription>
        </Alert>
      </VStack>
    </Container>
  );
}
